/* eslint-disable @typescript-eslint/no-explicit-any */
export type User = {
    id: number;
    companyId: number;
    email: string;
    name: string;
    profile: Role;
    super: boolean;
    company: Company;
    queues: Queue[];
    statusAvailable: boolean;
    isAttendant: boolean;
    onVacation: boolean;
    isActive: boolean;
    soundUrl: string | null;
    companyDistribuitionMethod: 'manual' | 'rotative';
    isSignatureEnabled: boolean;
};

export type Company = {
    id: number;
    planId: number;
    createdAt: string;
    updatedAt: string;
    dueDate: string;
    email: string;
    name: string;
    phone?: string;
    recurrence: string;
    status: boolean;
    // TODO: Update this
    schedules: QueueSchedule[];
    settings: Setting[];
};

export type Setting = {
    companyId: number;
    id: number;
    createdAt: string;
    updatedAt: string;
    key: string;
    value: string;
};

export const SettingScheduleType: string = 'scheduleType';
export const SettingIdleUserTimeoutInMinutes: string =
    'IdleUserTimeoutInMinutes';
export const SettingIdleConfiguration: string = 'IdleConfiguration';
export const SettingKanban: string = 'kanban';
export const SettingUserSignature: string = 'isSignatureEnabled';
export const SettingCloseReason: string = 'closeReason';
export const SettingDistribuitionMethod: string = 'distribuitionMethod';
export const SettingWebhookURL: string = 'webhookURL';

export type KanbanStage = {
    id: number;
    name: string;
    isDefault: boolean;
    order: number | undefined;
    companyId: number | undefined;
};

export type KanbanStages = {
    kanbanStages: KanbanStage[];
    count: number;
    hasMore: boolean;
};

export type SidePane = 'contact' | 'tags' | 'notes' | null;

export type CloseReason = {
    id: number;
    title: string;
    description: string;
};

export type CloseReasonResponse = {
    closeReasons: CloseReason[];
    count: number;
    hasMore: boolean;
};

export type Invoice = {
    id: number;
    detail: string;
    link: string | null;
    status: string;
    value: number;
    dueDate: string;
    paidDate: string | null;
    companyId: number;
    asaasHashId: string | null;
    createdAt: string;
    updatedAt: string | null;
};

export type Queue = {
    id: number;
    companyId: number;
    color: string;
    createdAt: string;
    updatedAt: string;
    name: string;
    greetingMessage: string;
    outOfHoursMessage: string;
    allowPreferAttendant: boolean;
    schedules: QueueSchedule[];
    users?: User[];
    timeoutSettings: QueueTimeoutSettingsType[];
};

export type QueueOption = {
    id: number;
    title: string;
    message: string;
    option: number;
    parentId: null;
    queueId: number;
    createdAt: string;
    updatedAt: string;
};

export type QueueSchedule = {
    endTime: string;
    startTime: string;
    weekday: string;
    weekdayEn: string;
};

export type Plan = {
    connections: number;
    id: number;
    name: string;
    queues: number;
    users: number;
    value: number;
    hidden?: boolean;
    createdAt: string;
    updatedAt: string;
};

export type Role = 'user' | 'admin' | 'supervisor';

export type WhatsAppStatus =
    | 'OPENING'
    | 'DISCONNECTED'
    | 'qrcode'
    | 'CONNECTED'
    | 'TIMEOUT'
    | 'PAIRING'
    | 'RESTRITO';

export type WhatsApp = {
    id: number;
    actualNumber: string | null;
    actualName: string | null;
    name: string;
    session: string | null;
    qrcode: string | null;
    status: WhatsAppStatus;
    /** Previsão de desbloqueio quando o canal está RESTRITO (estimativa) */
    restrictedUntil: string | null;
    flowIdentifier: string | null;
    retries: number;
    greetingMessage: string;
    farewellMessage: string;
    complationMessage: string;
    outOfHoursMessage: string;
    ratingMessage: string;
    provider: 'beta';
    isDefault: boolean;
    companyId: number;
    token: string;
    tokenMeta: string | null;
    channel: 'whatsapp';
    createdAt: string;
    updatedAt: string;
    queues: Queue[];
    waPhoneID: string | null;
    wabaID: string | null;
    oficial: boolean;
    /** Número oficial em coexistência (app WhatsApp Business + Cloud API) */
    coexistence: boolean;
    /** Última decisão da Meta sobre o nome verificado do número */
    nameStatus: MetaNameStatus | null;
    /** Nome verificado solicitado, enquanto não é aplicado em actualName */
    requestedName: string | null;
    nameRejectionReason: string | null;
    /** Nome aprovado que ainda precisa de um novo register na Cloud API */
    registerPending: boolean;
};

export type MetaNameStatus =
    | 'APPROVED'
    | 'REJECTED'
    | 'PENDING'
    | 'DEFERRED';

export type TicketStatus = 'open' | 'closed' | 'pending';

export type OnChatBotStatus = 'onchatbot';

export type Ticket = {
    id: number;
    status: TicketStatus;
    channel: 'whatsapp';
    unreadMessages: number;
    lastMessage: string;
    isGroup: boolean;
    userId: number;
    userIdClosed: null;
    contactId: number;
    whatsappId: number;
    queueId: number | null;
    chatbot: boolean;
    queueOptionId: null;
    companyId: number;
    uuid: string;
    createdAt: string;
    updatedAt: string;
    enabledMessages: boolean;
    officialTicket: boolean;
    contact: Contact;
    queue?: Queue;
    user: {
        id: number;
        name: string;
    };
    tags: Tag[];
    whatsapp: {
        name: string;
        actualName: string;
        actualNumber: string;
    };
    lastContactAnswer: string | null;
    lastUserAnswer: string | null;
    kanbanStage: KanbanStage;
    kanbanStageId: number;
};

export type TicketCounters = {
    openCount: number;
    openTickets: number[];
    onChatbotCount: number;
    onChatbotTickets: number[];
    pendingCount: number;
    pendingTickets: number[];
};

export type Contact = {
    id: number;
    name: string;
    number: string;
    email: string;
    profilePicUrl: string;
    attendantId?: number;
    updatedAt?: string;
    blockedAt?: string | null;
    extraInfo?: {
        id: number;
        name: string;
        value: string;
    }[];
    attendant?: {
        id: number;
        name: string;
    };
    isGroup: boolean;
    lidNumber?: string | null;
};

export type MessageButton = {
    text: string;
    /*
    type: string;
    reply: {
        id: string;
        title: string;
    };
    */
};
export type MessageListItem = {
    id: string;
    title: string;
    description?: string;
};
export type MessageList = {
    rows: MessageListItem[];
};

export type ContactObj = {
    contactId: number;
    name: string;
    number: string;
};

export type Message = {
    ack: number;
    body: string;
    channel: 'whatsapp';
    companyId: number;
    contact: Contact | null;
    contacts: ContactObj[];
    contactId: number | null;
    createdAt: string;
    dataJson: string;
    fromMe: boolean;
    id: string;
    isDeleted: boolean;
    mediaType:
        | 'image'
        | 'audio'
        | 'video'
        | 'file'
        | 'location'
        | 'system'
        | 'template'
        | 'locationMessage'
        | 'contactsArrayMessage'
        | 'contacts'
        | 'contactMessage'
        | 'extendedTextMessage';
    mediaUrl: string;
    participant: null;
    queue: Queue | null;
    queueId: null;
    quotedMsg: Message | null;
    quotedMsgId: string | null;
    buttons: MessageButton[] | null;
    list: MessageList[] | null;
    read: boolean;
    remoteJid: string;
    ticketId: number;
    updatedAt: string;
    statusError: string | null;
    timestampEditedAt: string;
};

export type EditMessageType = {
    newBody: string;
    messageId: string | undefined;
};

export type Note = {
    id: number;
    note: string;
    userId: number;
    contactId: number;
    ticketId: number;
    createdAt: string;
    updatedAt: string;
    user: {
        id: number;
        name: string;
        email: string;
    };
    contact: {
        id: number;
        name: string;
    };
    ticket: {
        id: number;
        status: TicketStatus;
        createdAt: string;
    };
};

export type TagContact = {
    id: number;
    contact: Contact;
};

export type Tag = {
    id: number;
    name: string;
    color: string;
    ticketsCount: number;
    companyId: number;
    createdAt: string;
    updatedAt: string;
    tickets?: TagContact[];
};

export type TicketTag = {
    ticketId: number;
    tagId: number;
    createdAt: string;
    updatedAt: string;
    ticket: Ticket;
    tag: Tag;
};

export type TagOption = {
    id: number;
    title: string;
    message: string;
    option: number;
    parentId: null;
    queueId: number;
    createdAt: string;
    updatedAt: string;
};

export type TemplateExtraInfo = {
    id: number;
    name: string;
    value: string;
    companyId: number;
    createdAt: string;
    updatedAt: string;
};

export type Schedule = {
    id: number;
    body: string;
    sendAt: string;
    sentAt: string | null;
    contactId: number;
    ticketId: number | null;
    userId: number;
    companyId: number;
    status: 'PENDENTE' | 'AGENDADA' | 'ENVIADA';
    createdAt: string;
    updatedAt: string;
    contact: {
        id: number;
        name: string;
    };
    user: {
        id: number;
        name: string;
    };
    mediaUrl: string | null;
    messageType: 'text' | 'audio' | 'video' | 'image';
    whatsappId?: number;
};

export type QuickMessage = {
    id: number;
    shortcode: string;
    message: string;
    isGlobal: boolean;
    mediaUrl?: string | null;
    mediaName?: string | null;
    mediaMimeType?: string | null;
};

export type WebsocketMessage = {
    action: 'create';
    contact: Contact;
    message: Message;
    ticket: Ticket;
};

export type MetaTemplate = {
    name: string;
    components: MetaTemplateComponent[];
    language: string;
    status: 'APPROVED' | 'PENDING' | 'REJECTED';
    category: 'MARKETING' | 'AUTHENTICATION' | 'UTILITY';
    id: string;
    parameter_format?: 'POSITIONAL' | 'NAMED';
};

export type MetaPaging = {
    cursors: {
        before?: string;
        after?: string;
    };
    next?: string;
    previous?: string;
};

export type MetaTemplateButtonObject = {
    type: string;
    text?: string;
};

export type MetaTemplateExampleObject = {
    header_handle?: string[];
    body_text?: string[][];
};

export type MetaTemplateComponent = {
    type: 'BODY' | 'FOOTER' | 'HEADER' | 'BUTTONS';
    text?: string;
    image?: string;
    link?: string;
    buttons?: MetaTemplateButtonObject[];
    format?: string;
    example?: MetaTemplateExampleObject;
};

export type MetaTemplateComponentParameter = {
    type: string;
    text?: string;
    [key: string]: any;
};

export type MetaTemplateComponentType = {
    type: string;
    parameters: MetaTemplateComponentParameter[];
};

export type IdleConfiguration = {
    startMinutes: number;
    endMinutes?: number;
    priority: number;
    color: string;
    originalIndex?: number;
    field?: 'updatedAt' | 'lastContactAnswer' | 'lastUserAnswer';
};

export type QueueTimeoutSettingsType = {
    idleMinutes: number;
    originalIndex?: number;
    field:
        | 'updatedAt'
        | 'compareContactToUser'
        | 'compareUserToContact'
        | 'lastUserAnswer';
};

export type CampaignTemplateParamType =
    | 'contact_name'
    | 'contact_number'
    | 'fixed_value';

export type CampaignFormType = {
    name: string;
    channelId: number | null;
    channelName: string;
    template: MetaTemplate | null;
    templateParams: {
        order: number;
        name: string;
        type: CampaignTemplateParamType;
        value: string;
    }[];
    closeTicketIfExist: boolean;
    flowName: string;
};

export interface FormData {
    date: { start: Date; end: Date };
}

export interface ReportListItem {
    id: string;
    body: string;
    statusError: string;
    ticketId: number;
    createdAt: string;
    ticket: {
        contactId: number;
        uuid: string;
        contact: {
            id: number;
            name: string;
            number: string;
        };
    };
}

export interface ReportTemplateMessages {
    count: number;
    hasMore: boolean;
    messages: ReportListItem[];
}
export interface ReportTemplateChart {
    status: string;
    total: string;
}

export interface Audit {
    id: string;
    companyId: number;
    action: string;
    area: string;
    userId: number;
    userEmail: string;
    description: string;
    payload: string;
    createdAt: string;
    updatedAt: string;
}
